import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  listProposedPatterns,
  getPatternDetail,
  decidePattern,
  type PatternRow,
  type PatternStatus,
} from "@/lib/admin.patterns.functions";

export const Route = createFileRoute("/admin/patterns")({
  component: AdminPatternsPage,
});

const STATUSES: { value: PatternStatus; label: string }[] = [
  { value: "proposed", label: "Pending" },
  { value: "active", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

function AdminPatternsPage() {
  const [status, setStatus] = useState<PatternStatus>("proposed");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const listFn = useServerFn(listProposedPatterns);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "patterns", { status }],
    queryFn: () => listFn({ data: { status, limit: 200 } }),
  });

  const rows = useMemo(() => data?.patterns ?? [], [data]);

  return (
    <div className="space-y-6">
      <div>
        <p className="overline mb-2">Learning loop</p>
        <h2 className="font-serif text-2xl text-primary mb-4">Learned patterns</h2>
        <div className="flex flex-wrap gap-2 mb-4 text-sm">
          {STATUSES.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => {
                setStatus(s.value);
                setSelectedId(null);
              }}
              className={`px-3 py-1.5 border border-border transition-colors ${
                status === s.value
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-muted"
              }`}
            >
              {s.label}
            </button>
          ))}
          {data && (
            <span className="text-xs text-ink-tertiary ml-auto font-mono self-center">
              {rows.length} shown
            </span>
          )}
        </div>
      </div>

      {isLoading && <p className="text-sm text-ink-tertiary">Loading…</p>}
      {error && (
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : "Failed to load patterns"}
        </p>
      )}

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-6">
          <ul className="border border-border divide-y divide-border bg-card max-h-[70vh] overflow-y-auto">
            {rows.map((p) => (
              <PatternListItem
                key={p.id}
                pattern={p}
                selected={selectedId === p.id}
                onSelect={() => setSelectedId(p.id)}
              />
            ))}
            {rows.length === 0 && (
              <li className="p-6 text-center text-ink-tertiary text-sm">
                No patterns in this state.
              </li>
            )}
          </ul>
          <div className="border border-border bg-card p-4 min-h-[40vh]">
            {selectedId ? (
              <PatternDetailPanel id={selectedId} listStatus={status} />
            ) : (
              <p className="text-sm text-ink-tertiary">
                Select a pattern on the left to see its evidence and review.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PatternListItem({
  pattern,
  selected,
  onSelect,
}: {
  pattern: PatternRow;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={`w-full text-left p-4 text-sm ${
          selected ? "bg-muted" : "hover:bg-muted/50"
        }`}
      >
        <div className="flex items-baseline justify-between gap-3">
          <span
            className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase ${
              pattern.status === "active"
                ? "bg-emerald-200 text-emerald-900"
                : pattern.status === "rejected"
                  ? "bg-muted text-ink-tertiary"
                  : "bg-amber-200 text-amber-900"
            }`}
          >
            {pattern.status}
          </span>
          <span className="text-xs text-ink-tertiary font-mono">
            conf {pattern.confidence.toFixed(2)} · ev {pattern.evidence_count}
          </span>
        </div>
        <p className="mt-2 text-ink-primary line-clamp-3">{pattern.hypothesis}</p>
        <p className="mt-1 text-[11px] text-ink-tertiary font-mono">
          {new Date(pattern.created_at).toLocaleString("en-GB")}
        </p>
      </button>
    </li>
  );
}

function PatternDetailPanel({
  id,
  listStatus,
}: {
  id: string;
  listStatus: PatternStatus;
}) {
  const qc = useQueryClient();
  const detailFn = useServerFn(getPatternDetail);
  const decideFn = useServerFn(decidePattern);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "pattern", id],
    queryFn: () => detailFn({ data: { id } }),
  });

  const [note, setNote] = useState("");
  const [hypothesis, setHypothesis] = useState<string | null>(null);
  const [confidence, setConfidence] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const decide = useMutation({
    mutationFn: (input: {
      decision: "approve" | "reject" | "edit";
    }) =>
      decideFn({
        data: {
          id,
          decision: input.decision,
          note: note.trim() || undefined,
          hypothesis:
            input.decision === "edit" && hypothesis != null
              ? hypothesis
              : undefined,
          confidence:
            input.decision === "edit" && confidence != null && confidence !== ""
              ? Number(confidence)
              : undefined,
        },
      }),
    onSuccess: (_res, vars) => {
      const verb =
        vars.decision === "approve"
          ? "approved"
          : vars.decision === "reject"
            ? "rejected"
            : "updated";
      toast.success(`Pattern ${verb}`);
      setNote("");
      setEditing(false);
      setHypothesis(null);
      setConfidence(null);
      qc.invalidateQueries({ queryKey: ["admin", "patterns"] });
      qc.invalidateQueries({ queryKey: ["admin", "pattern", id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) {
    return <p className="text-sm text-ink-tertiary">Loading pattern…</p>;
  }
  if (error) {
    return (
      <p className="text-sm text-destructive">
        {error instanceof Error ? error.message : "Failed to load pattern"}
      </p>
    );
  }
  if (!data) return null;

  const p = data.pattern;
  const meta = parseJson(p.metadata);
  const reviews = Array.isArray(meta?.reviews) ? (meta.reviews as ReviewLogEntry[]) : [];

  return (
    <div className="space-y-5 text-sm">
      <div>
        <div className="flex items-baseline justify-between gap-3 mb-2">
          <span
            className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase ${
              p.status === "active"
                ? "bg-emerald-200 text-emerald-900"
                : p.status === "rejected"
                  ? "bg-muted text-ink-tertiary"
                  : "bg-amber-200 text-amber-900"
            }`}
          >
            {p.status}
          </span>
          <span className="text-xs text-ink-tertiary font-mono">
            conf {p.confidence.toFixed(2)} · support {p.support}
          </span>
        </div>
        {editing ? (
          <div className="space-y-2">
            <textarea
              value={hypothesis ?? p.hypothesis}
              onChange={(e) => setHypothesis(e.target.value)}
              rows={4}
              className="w-full border border-border bg-background p-2 text-sm"
            />
            <label className="flex items-center gap-2 text-xs text-ink-secondary">
              Confidence
              <input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={confidence ?? p.confidence.toString()}
                onChange={(e) => setConfidence(e.target.value)}
                className="border border-border bg-background px-2 py-1 w-24 text-xs font-mono"
              />
            </label>
          </div>
        ) : (
          <p className="text-ink-primary whitespace-pre-wrap">{p.hypothesis}</p>
        )}
      </div>

      <ScopeSummary
        level={p.scope_level}
        discipline={p.scope_discipline}
        institutionBucket={p.scope_institution_bucket}
        relatedRule={p.related_rule}
      />

      <div>
        <p className="overline mb-2">Evidence ({p.evidence.length})</p>
        {p.evidence.length === 0 ? (
          <p className="text-xs text-ink-tertiary">No supporting marker feedback linked.</p>
        ) : (
          <ul className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {p.evidence.map((e) => (
              <li
                key={e.id}
                className="border border-border bg-background p-3 text-xs space-y-1"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-mono text-ink-tertiary">
                    weight {e.weight.toFixed(2)}
                    {e.final_grade ? ` · grade ${e.final_grade}` : ""}
                  </span>
                  {e.feedback_created_at && (
                    <span className="font-mono text-ink-tertiary">
                      {new Date(e.feedback_created_at).toLocaleDateString("en-GB")}
                    </span>
                  )}
                </div>
                {e.marker_comment && (
                  <p className="text-ink-primary whitespace-pre-wrap">{e.marker_comment}</p>
                )}
                {e.draft_segment && (
                  <details className="text-ink-tertiary">
                    <summary className="cursor-pointer">Draft excerpt</summary>
                    <p className="mt-1 whitespace-pre-wrap font-mono text-[11px]">
                      {e.draft_segment.slice(0, 600)}
                      {e.draft_segment.length > 600 ? "…" : ""}
                    </p>
                  </details>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {reviews.length > 0 && (
        <div>
          <p className="overline mb-2">Review history</p>
          <ul className="space-y-1 text-xs text-ink-secondary">
            {reviews.map((r, i) => (
              <li key={i} className="font-mono">
                {new Date(r.at).toLocaleString("en-GB")} · {r.decision}
                {r.from_status ? ` (from ${r.from_status})` : ""}
                {r.note ? ` — ${r.note}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-2 border-t border-border pt-4">
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder="Optional admin note…"
          className="w-full border border-border bg-background p-2 text-xs font-mono"
        />
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => decide.mutate({ decision: "approve" })}
            disabled={decide.isPending || p.status === "active"}
            className="bg-primary text-primary-foreground px-3 py-1.5 text-xs disabled:opacity-50"
          >
            {decide.isPending ? "Saving…" : "Approve"}
          </button>
          <button
            type="button"
            onClick={() => decide.mutate({ decision: "reject" })}
            disabled={decide.isPending || p.status === "rejected"}
            className="border border-border px-3 py-1.5 text-xs disabled:opacity-50"
          >
            Reject
          </button>
          {editing ? (
            <>
              <button
                type="button"
                onClick={() => decide.mutate({ decision: "edit" })}
                disabled={decide.isPending}
                className="border border-border px-3 py-1.5 text-xs disabled:opacity-50"
              >
                Save edit
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setHypothesis(null);
                  setConfidence(null);
                }}
                className="px-3 py-1.5 text-xs text-ink-tertiary"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="border border-border px-3 py-1.5 text-xs"
            >
              Edit
            </button>
          )}
        </div>
        {listStatus !== "proposed" && (
          <p className="text-[11px] text-ink-tertiary">
            This pattern is already {p.status}. Approve/Reject will move it again.
          </p>
        )}
      </div>
    </div>
  );
}

function ScopeSummary({
  level,
  discipline,
  institutionBucket,
  relatedRule,
}: {
  level: number[] | null;
  discipline: string[] | null;
  institutionBucket: string | null;
  relatedRule: { id: string; title: string; type: string } | null;
}) {
  const parts: string[] = [];
  if (level && level.length) parts.push(`Level ${level.join(", ")}`);
  if (discipline && discipline.length) parts.push(discipline.join(", "));
  if (institutionBucket) parts.push(institutionBucket);
  if (parts.length === 0 && !relatedRule) return null;
  return (
    <div className="text-xs text-ink-secondary space-y-1">
      {parts.length > 0 && (
        <p>
          <span className="text-ink-tertiary font-mono">Scope:</span>{" "}
          {parts.join(" · ")}
        </p>
      )}
      {relatedRule && (
        <p>
          <span className="text-ink-tertiary font-mono">Related rule:</span>{" "}
          {relatedRule.title}{" "}
          <span className="text-ink-tertiary">({relatedRule.type})</span>
        </p>
      )}
    </div>
  );
}

type ReviewLogEntry = {
  at: string;
  decision: string;
  note?: string | null;
  from_status?: string | null;
};

function parseJson(value: string | null): Record<string, unknown> | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}
